<template>
  <available-section class="report-employees" section="reports">
    <div class="report-employees">
      <div class="report-employees__header">
        <h1>Отчет по сотрудникам</h1>
        <form v-on:submit.prevent="requestReport" class="report-employees__form">
          <div class="report-employees__row">
            <date-picker-dialog
              range
              v-bind:value="period"
              v-on:input="onInputPeriod"
              v-on:close="onCloseDatePickerDialog"
            >
              <template v-slot:trigger="{ listenerObject }">
                <button
                  type="button"
                  v-on="listenerObject"
                  class="report-employees__search"
                >
                  <div v-if="period.length >= 1">{{periodTitle}}</div>
                  <div v-else>Выберите период</div>
                </button>
              </template>
            </date-picker-dialog>
            <button
              type="button"
              class="report-employees__search__exit"
              v-if="period.length >= 1"
              v-on:click="period = []"
            />
          </div>
          <div>
            <button
              type="button"
              v-on:click="requestByMonth"
              class="report-employees__tag"
              v-bind:class="{ active: isMonthAgoToToday }">Mесяц</button>
          </div>
          <div class="report-employees__select">
            <template v-if="!positionListLoaded" />
            <the-select
              v-else-if="positionListAvailable"
              title="Должность"
              v-model="positionId"
              v-bind:options="positionList"
            />
            <div v-else>У вас недостаточно прав для фильтрации по должностям</div>
          </div>
          <button type="submit" class="report-employees__button">Найти</button>
        </form>
      </div>
      <div class="report-employees__body">
        <div class="report-employees__table">
          <div class="report-employees__table__head">
            <div>ФИО</div>
            <div>Кол-во визитов</div>
            <div>Кол-во первичных визитов</div>
            <div>Кол-во вторичных визитов</div>
            <div>Сумма</div>
          </div>
          <div
            v-for="(employee, key) in employeeList"
            v-bind:key="key"
            class="report-employees__table__row"
          >
            <div data-label="ФИО">{{employee.name}}
              <template v-if="employee.positionNames">({{employee.positionNames}})</template>
            </div>
            <div data-label="Кол-во визитов">{{employee.visitCount}}</div>
            <div data-label="Кол-во первичных визитов">{{employee.firstVisitCount}}</div>
            <div data-label="Кол-во вторичных визитов">{{employee.secondVisitCount}}</div>
            <div data-label="Сумма">{{employee.priceSum}}</div>
          </div>
        </div>
      </div>
    </div>
  </available-section>
</template>

<script>
import TheSelect from '@/components/TheSelect.vue';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import { formatDate } from '@/utils/timeFormat';
import notifierMixin from '@/mixins/notifierMixin';
import AvailableSection from '@/views/ClinicPanel/AvailableSection.vue';

export default {
  components: { AvailableSection, DatePickerDialog, TheSelect },
  computed: {
    /**
     * @description Отображение периода в читаемом виде.
     * @returns {string}
     */
    periodTitle() {
      const [start, end] = this.period.map(formatDate);
      if (!end) {
        return `${start}`;
      }
      return `${start}  —  ${end}`;
    },
    isMonthAgoToToday() {
      if (this.period.length !== 2) {
        return false;
      }
      const now = new Date().toDateString();
      const [firstDate, secondDate] = this.period.slice().sort((a, b) => a - b);
      if (secondDate.toDateString() !== now) {
        return false;
      }
      const diffYears = secondDate.getFullYear() - firstDate.getFullYear();
      const diffMonth = diffYears * 12 - firstDate.getMonth() + secondDate.getMonth();
      return diffMonth === 1;
    },
  },
  created() {
    this.fetchFilters();
    this.requestByMonth();
    this.requestReport();
  },
  data() {
    return {
      period: [],
      positionId: '',
      positionList: [],
      isLoading: false,
      employeeList: [],
      positionListAvailable: false,
      positionListLoaded: false,
    };
  },
  methods: {
    requestByMonth() {
      const date = new Date();
      const currentMonth = date.getMonth();
      const beforeMonthDate = new Date((new Date()).setMonth(currentMonth - 1));
      this.period = [beforeMonthDate, date];
    },
    /**
     * @description Получить список должностей клиники.
     * @returns {[]}
     */
    fetchFilters() {
      const onSuccess = (positions) => {
        this.positionList = positions.map((position) => ({
          title: position.name,
          value: position.id,
        }));
        this.positionListLoaded = true;
        this.positionListAvailable = true;
      };
      const onFail = (data) => {
        this.notifyFail(data.title);
      };
      this.$reportGateway.getFilterForReportByEmployees().then((either) => {
        either.fold(onFail, onSuccess);
      });
      return [];
    },
    /**
     * @description Запросить отчет
     */
    requestReport() {
      const onSuccess = (employees) => {
        this.employeeList = employees;
      };
      const onFail = (data) => {
        this.notifyFail(data.title);
      };
      this.$reportGateway.getReportByEmployees(
        this.period,
        this.positionId,
      ).then((either) => {
        either.fold(onFail, onSuccess);
      });
    },
    /**
     * @description Обработать изменение периода.
     * @param {Date[]} value
     * @returns {void}
     */
    onInputPeriod(value) {
      this.period = value.sort(
        (first, second) => Number(first.getTime() > second.getTime()),
      );
    },
    /**
     * @description Обработать закрытие диалога выборы даты.
     * @returns {void}
     */
    onCloseDatePickerDialog() {
      if (this.period.length === 1) {
        const [left] = this.period;
        this.onInputPeriod([left, left]);
      }
    },
  },
  mixins: [notifierMixin],
  name: 'ClinicPanelReportEmployees',
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.report-employees {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  &__header {
    background: #F2F5F8;
    width: 100%;
    padding: 38px 20px 48px 23px;
  }
  &__form {
    display: flex;
    flex-flow: row wrap;
    align-items: flex-end;
    gap: 23px;
    margin: 30px 0 0 0;
  }
  &__row {
    display: flex;
    flex-direction: row;
  }
  &__search {
    border: 1px solid #C2CFE0;
    border-radius: 4px;
    background: #FFFFFF;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    height: 40px;
    min-width: 250px;
    width: 100%;
    padding: 0 0 0 10px;
    display: flex;
    align-items: center;
    position: relative;
    &:after {
      content: '';
      background-color: #EAEFF3;
      border-radius: 0 4px 4px 0;
      width: 38px;
      height: 38px;
      position: absolute;
      right: 0;
      overflow: hidden;
      background-image: url("@/assets/img/calendar-grey.svg");
      background-position: center;
    }
    &__exit {
      width: 40px;
      height: 40px;
      border: 1px solid #C2CFE0;
      background-color: #EAEFF3;
      border-radius: 4px;
      background-image: url("@/assets/img/exit-dark-grey.svg");
      background-position: center;
      margin: 0 0 0 -2px;
    }
  }
  &__tag {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    background: #EAEFF3;
    border-radius:4px;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    color: #192A3E;
    padding: 0 13px;
    margin: 0 10px 0 0;
    &:hover,
    &.active {
      background: #0489E0;
      color: #FFFFFF;
    }
  }
  &__button {
    @include buttons.btn-blue(250px, 40px, 18px);
  }
  &__select {
    max-width: 500px;
    width: 100%;
  }
  &__body {
    padding: 48px 20px 150px 23px;
  }
  &__table {
    &__head,
    &__row {
      width: 100%;
      text-align: left;
      cursor: pointer;
      display: grid;
      grid-template-columns: 1fr 250px 250px 250px 250px;
      align-items: center;
      font-size: 14px;
      line-height: 17px;
    }
    &__head {
      width: 100%;
      font-weight: 700;
      border-bottom: 2px solid #0489e0;
      padding: 10px 0;
      div {
        padding: 0 8px;
      }
    }
    &__row {
      border-bottom: 1px solid #eaeff3;
      padding: 12px 0;
      div {
        padding: 0 8px;
      }
      &:hover {
        background: #eaeff3;
      }
    }
  }
}
@media screen and (max-width: 1500px) {
  .report-employees {
    &__table {
      &__head,
      &__row {
        grid-template-columns: 400px repeat(4, 1fr);
      }
    }
  }
}
@media screen and (max-width: 1040px) {
  .report-employees {
    &__table {
      &__head {
        display: none;
      }
      &__row {
        grid-template-columns: 1fr;
        grid-gap: 5px;
        margin: 0 0 20px 0;
        div {
          display: flex;
          justify-content: space-between;
          text-align: right;
        }
        div:before {
          content: attr(data-label);
          font-weight: 700;
          margin-right: 20px;
        }
      }
    }
  }
}
@media screen and (max-width: 600px) {
  .report-employees {
    &__form {
      flex-direction: column;
      align-items: flex-start;
    }
    &__select {
      width: 100%;
    }
    &__button {
      width: 100%;
    }
  }
}
@media screen and (max-width: 480px) {
  .report-employees {
    &__header {
      padding: 40px 20px;
    }
    &__body {
      padding: 40px 20px 100px 20px;
    }
  }
}

</style>
